import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Footer } from '@components/molecules/Footer';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import { Container } from '@components/atoms/Container';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Card } from '@components/atoms/Card';
import { Accordion } from '@components/atoms/Accordion';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { useServicesCart } from '@hooks/useServicesCart';
import { useThemes } from '@hooks/useThemes';
import { useData } from '@hooks/useData';
import { useGetAllStoreSchedulableWorkers } from '@services/useGetAllStoreSchedulableWorkers';
import { formatDateToLongLocalized, formatPrice } from '@utils/format';
import styles from './ServicesCartContainer.module.css';
import headerStyles from './CartHeader.module.css';
import totalStyles from './CartTotal.module.css';

interface ServicesCartContainerProps {
    step: number;
    title: string;
    action: JSX.Element | JSX.Element[];
    children: string | JSX.Element | JSX.Element[];
    displayTotal?: boolean;
    displayOnlyTotalValues?: boolean;
}

export const ServicesCartContainer: FC<ServicesCartContainerProps> = ({
    step,
    title,
    action,
    children,
    displayTotal = true,
    displayOnlyTotalValues = false,
}) => {
    const { storeName, worker, setWorker } = useServicesCart();
    const { allStoreSchedulableWorkers } = useData();

    const { loading: loadingAllStoreSchedulableWorkers, lastResponseTime: lastTimeAllStoreSchedulableWorkers } =
        useGetAllStoreSchedulableWorkers();

    useEffect(() => {
        // check if previously selected worker is still present in the service
        // because the worker could have been removed
        if (
            lastTimeAllStoreSchedulableWorkers !== null &&
            !loadingAllStoreSchedulableWorkers &&
            !allStoreSchedulableWorkers?.data?.find((item) => item.uuid === worker?.uuid)
        ) {
            setWorker(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastTimeAllStoreSchedulableWorkers, loadingAllStoreSchedulableWorkers]);

    return (
        <div className="page" data-testid="services-cart-container">
            <CartHeader activeStep={step} />
            <div className={`d-flex flex-column flex-grow-1 ${styles['shopping-cart']} bg-secondary`}>
                <div className="flex-grow-1 container px-3 py-4 pb-lg-5">
                    <div className="d-flex align-items-center mb-2 mb-md-4 gap-4 justify-content-between justify-content-lg-start">
                        <Typography
                            classes="mb-0"
                            tagVariant={TypographyTagVariants.h1}
                            appearanceLevel={TypographyAppearanceVariants.level3}>
                            {title}
                        </Typography>
                    </div>
                    <Typography classes="mb-4" fontSize="16" fontWeight="800">
                        {storeName || ''}
                    </Typography>
                    <div className="row">
                        <div className={displayTotal ? 'col-12 col-lg-8' : 'col-12'}>
                            <div className={styles['shopping-cart-data-container']}>{children}</div>
                        </div>
                        {displayTotal ? (
                            <div className={`${styles['total-container']} col-12 col-lg-4`}>
                                <CartTotal displayOnlyTotalValues={displayOnlyTotalValues} />
                                <div className="order-summary-action-container">{action}</div>
                            </div>
                        ) : (
                            <div className="text-center mt-4 py-2">{action}</div>
                        )}
                    </div>
                </div>
                <hr className="d-none d-lg-block m-0" />
            </div>

            <Footer mode="normal" />
        </div>
    );
};

interface CartHeaderProps {
    activeStep: number;
}

const CartHeader: FC<CartHeaderProps> = ({ activeStep }) => {
    const { useGrayscaleTheme } = useThemes();
    const {
        request: requestAllStoreSchedulableWorkers,
        lastResponseTime: lastResponseAllStoreSchedulableWorkers,
        error: errorAllStoreSchedulableWorkers,
    } = useGetAllStoreSchedulableWorkers();
    const { allSettings, allStoreSchedulableWorkers } = useData();
    const { storeUuid, displayWorkerStep, setDisplayWorkerStep, scheduleType } = useServicesCart();
    const { t } = useTranslation();

    // check if employees step should be displayed
    // - if module is active
    // - if store has employees
    useEffect(() => {
        if (allSettings?.data?.module_services?.schedule_by_employee && storeUuid) {
            requestAllStoreSchedulableWorkers({ uuid: storeUuid });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSettings?.data, storeUuid]);

    useEffect(() => {
        if (lastResponseAllStoreSchedulableWorkers) {
            if (!errorAllStoreSchedulableWorkers && allStoreSchedulableWorkers?.data) {
                setDisplayWorkerStep(allStoreSchedulableWorkers.data.length > 0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseAllStoreSchedulableWorkers]);

    const translations = [
        t('services-cart-header-component.step.services'),
        // worker: optional step
        displayWorkerStep ? t('services-cart-header-component.step.employee') : undefined,
        t('services-cart-header-component.step.scheduling'),
        // prebook or pay
        scheduleType === 'preBook'
            ? t('services-cart-header-component.step.prebook')
            : t('services-cart-header-component.step.payment'),
        t('services-cart-header-component.step.confirm'),
    ].filter((item) => item !== undefined);

    const logoItem = allSettings?.data?.layouts?.find((item) => item.name === 'logo');
    const logoSrc = logoItem?.value;

    return (
        <header
            className={`${headerStyles['shopping-cart-steps']} ${useGrayscaleTheme ? headerStyles['theme-grayscale'] : ''}`}
            data-testid="services-cart-header">
            <Container classes="px-0">
                <>
                    <div className="d-flex flex-column flex-md-row align-items-center justify-content-center justify-content-lg-start position-relative">
                        {/* go back to previous page - on smaller screens */}
                        <button className={`${headerStyles['go-back']} d-block d-md-none`} onClick={() => window.history.back()}>
                            <i className="fa-thin fa-arrow-left"></i>
                        </button>
                        {/* logo */}
                        <Anchor href="" classes={headerStyles['logo-container']} mode={AnchorMode.none}>
                            <img className={headerStyles['brand-logo']} src={logoSrc} alt={allSettings?.data?.marketplaceName} />
                        </Anchor>
                        {/* steps */}
                        <div className={`nav ${headerStyles['nav']}`}>
                            <ul>
                                {translations.map((stepName, index) => {
                                    const currentStep = index + 1;
                                    return (
                                        <li
                                            key={`step-${currentStep}`}
                                            className={currentStep === activeStep ? headerStyles.active : ''}>
                                            <Typography
                                                classes={headerStyles['number']}
                                                tagVariant={TypographyTagVariants.span}
                                                fontSize="12">
                                                {`${currentStep}`}
                                            </Typography>
                                            <Typography
                                                classes={headerStyles['text']}
                                                tagVariant={TypographyTagVariants.span}
                                                fontSize="10"
                                                breakpointsStyles={{ xl: { fontSize: '12' } }}>
                                                {stepName}
                                            </Typography>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                </>
            </Container>
        </header>
    );
};

interface CartTotalProps {
    displayOnlyTotalValues?: boolean;
}

const CartTotal: FC<CartTotalProps> = ({ displayOnlyTotalValues }) => {
    const pageSize = useMediaQuery();
    const { useGrayscaleTheme } = useThemes();
    const { servicesCartTotals } = useData();
    const { t } = useTranslation();

    const translations = {
        summaryLabel: t('services-cart-total-component.summary.label'),
        servicesAmount: t('services-cart-total-component.services.amount', { count: servicesCartTotals?.data?.n_services }),
        taxesInfo: t('services-cart-total-component.taxes-info'),
        totalLabel: t('services-cart-total-component.total.label'),
    };

    const services =
        servicesCartTotals?.data?.services && servicesCartTotals.data.services.length > 0
            ? servicesCartTotals.data.services.map((service) => {
                  return {
                      name: service.name || '',
                      quantity: service.quantity || 0,
                      price: service.price,
                  };
              })
            : [];

    return (
        <Card
            classes={
                `${useGrayscaleTheme ? styles['theme-grayscale'] : ''}` +
                ` ${totalStyles['shopping-cart-total-card']} ${['xs', 'sm', 'md'].includes(pageSize) ? 'border border-0 ' : 'border'}`
            }>
            {!displayOnlyTotalValues ? (
                <>
                    {services.length === 0 ? (
                        <CartSummary services={services} />
                    ) : (
                        <>
                            {/* services summary - visible in big screens only */}
                            <div className="d-none d-lg-block">
                                {services.length > 0 && (
                                    <Typography
                                        appearanceLevel={TypographyAppearanceVariants.level8}
                                        classes={`${totalStyles.indent} pt-3 mb-0`}>
                                        {translations.summaryLabel}
                                    </Typography>
                                )}
                                <CartSummary services={services} />
                            </div>
                            {/* services summary accordion - visible in small screens only */}
                            <Accordion
                                id="services-summary"
                                arrowOnClosed="up"
                                items={[
                                    {
                                        id: 'collapseServicesSummary',
                                        buttonContent: (
                                            <Typography appearanceLevel={TypographyAppearanceVariants.level8} classes="mb-0">
                                                {translations.summaryLabel}
                                            </Typography>
                                        ),
                                        bodyContent: <CartSummary services={services} />,
                                    },
                                ]}
                                customClasses={{
                                    accordion: 'd-lg-none',
                                    accordionButton: `${totalStyles.indent} border-bottom pt-2 pb-2`,
                                    accordionBody: 'p-0',
                                }}
                            />
                        </>
                    )}
                </>
            ) : (
                <></>
            )}

            {/* total value */}
            <div className={`d-flex pt-2 pb-3 ${totalStyles.indent}`} data-testid="total">
                <div className="align-content-end">
                    <Typography classes="mb-0" appearanceLevel={TypographyAppearanceVariants.level8}>
                        {translations.totalLabel}
                    </Typography>
                    <Typography classes="mb-0" fontSize="12" color="muted">
                        {translations.servicesAmount}
                    </Typography>
                </div>
                <div className="text-end flex-grow-1">
                    <Typography classes={`${styles.price} mb-0`} fontSize="24" fontWeight="800">
                        {formatPrice(servicesCartTotals?.data?.total)}
                    </Typography>
                    <Typography classes="mb-0" fontSize="12" color="muted">
                        {translations.taxesInfo}
                    </Typography>
                </div>
            </div>
        </Card>
    );
};

interface CartSummaryProps {
    services: {
        name: string;
        quantity: number;
        price?: number;
        duration?: string;
    }[];
}

const CartSummary: FC<CartSummaryProps> = ({ services }) => {
    const { servicesCartTotals } = useData();
    const { worker, day, timeSlot } = useServicesCart();
    const { t, i18n } = useTranslation();

    const translations = {
        quantityLabel: t('services-cart-total-component.cart-summary.quantity-label'),
        durationLabel: t('services-cart-total-component.cart-summary.duration-label'),
        minutesLabel: t('services-cart-total-component.cart-summary.minutes-label', {
            count: servicesCartTotals?.data?.duration_min,
        }),
        workerLabel: t('services-cart-total-component.cart-summary.worker-label'),
        dayLabel: t('services-cart-total-component.cart-summary.day-label'),
        timeLabel: t('services-cart-total-component.cart-summary.time-label'),
    };

    useEffect(() => {
        if (servicesCartTotals?.data?.duration_min !== undefined) {
            translations.minutesLabel = t('services-cart-total-component.cart-summary.minutes-label', {
                count: servicesCartTotals?.data?.duration_min,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [servicesCartTotals?.data?.duration_min]);

    return (
        <>
            {services.length > 0 && (
                <>
                    <div className="border-bottom pb-2">
                        <div className={`${totalStyles['product-summary']}`} data-testid="services-list">
                            {services.map((service, index) => (
                                <div key={`order-item-${index}`} className={totalStyles.indent}>
                                    {index !== 0 && <hr className="my-0" />}
                                    <div className="my-3">
                                        <div className="d-grid flex-grow-1">
                                            <Typography classes="text-truncate mb-2" fontSize="16" color="dark" fontWeight="900">
                                                {service.name}
                                            </Typography>
                                            <div className="d-flex justify-content-between">
                                                <Typography classes="text-truncate mb-0" fontSize="14" color="muted">
                                                    <>{`${translations.quantityLabel} ${service.quantity}`}</>
                                                </Typography>
                                                <Typography
                                                    classes="text-truncate mb-0"
                                                    fontSize="14"
                                                    color="muted"
                                                    align="right">
                                                    {formatPrice(service.price)}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* duration */}
                    <div className="border-bottom">
                        <div className={`${totalStyles['product-summary']}`}>
                            <div className={totalStyles.indent}>
                                <div className="my-4 py-2">
                                    <div className="d-grid flex-grow-1">
                                        <Typography classes="text-truncate" fontSize="16" color="dark" fontWeight="900">
                                            <>
                                                <Typography
                                                    classes="text-truncate"
                                                    fontSize="16"
                                                    color="dark"
                                                    tagVariant={TypographyTagVariants.span}>
                                                    {translations.durationLabel}
                                                </Typography>{' '}
                                                <Typography
                                                    classes="text-truncate"
                                                    fontSize="16"
                                                    color="dark"
                                                    fontWeight="900"
                                                    tagVariant={TypographyTagVariants.span}>
                                                    {`${translations.minutesLabel}`}
                                                </Typography>
                                            </>
                                        </Typography>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* worker */}
                    {worker?.name && (
                        <div className="border-bottom">
                            <div className={`${totalStyles['product-summary']}`}>
                                <div className={totalStyles.indent}>
                                    <div className="my-4 py-2">
                                        <div className="d-grid flex-grow-1">
                                            <Typography classes="text-truncate" fontSize="16" color="dark" fontWeight="900">
                                                <>
                                                    <Typography
                                                        classes="text-truncate"
                                                        fontSize="16"
                                                        color="dark"
                                                        tagVariant={TypographyTagVariants.span}>
                                                        {translations.workerLabel}
                                                    </Typography>{' '}
                                                    <Typography
                                                        classes="text-truncate"
                                                        fontSize="16"
                                                        color="dark"
                                                        fontWeight="900"
                                                        tagVariant={TypographyTagVariants.span}>
                                                        {worker?.name}
                                                    </Typography>
                                                </>
                                            </Typography>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* day and time slot */}
                    {day && (
                        <div className="border-bottom">
                            <div className={`${totalStyles['product-summary']}`}>
                                <div className={totalStyles.indent}>
                                    <div className="my-4 py-2">
                                        <div className="d-grid flex-grow-1">
                                            <Typography classes="text-truncate" fontSize="16" color="dark" fontWeight="900">
                                                <>
                                                    <Typography
                                                        classes="text-truncate"
                                                        fontSize="16"
                                                        color="dark"
                                                        tagVariant={TypographyTagVariants.span}>
                                                        {translations.dayLabel}
                                                    </Typography>{' '}
                                                    <Typography
                                                        classes="text-truncate"
                                                        fontSize="16"
                                                        color="dark"
                                                        fontWeight="900"
                                                        tagVariant={TypographyTagVariants.span}>
                                                        {formatDateToLongLocalized(new Date(day), i18n.language)}
                                                    </Typography>
                                                </>
                                            </Typography>
                                            {timeSlot && (
                                                <Typography classes="text-truncate" fontSize="16" color="dark" fontWeight="900">
                                                    <>
                                                        <Typography
                                                            classes="text-truncate"
                                                            fontSize="16"
                                                            color="dark"
                                                            tagVariant={TypographyTagVariants.span}>
                                                            {translations.timeLabel}
                                                        </Typography>{' '}
                                                        <Typography
                                                            classes="text-truncate"
                                                            fontSize="16"
                                                            color="dark"
                                                            fontWeight="900"
                                                            tagVariant={TypographyTagVariants.span}>
                                                            {timeSlot}
                                                        </Typography>
                                                    </>
                                                </Typography>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
};
